import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useTreeMutations } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import React from 'react'

/**
 * Custom hook to handle track changes filter logic.
 * It provides a function to handle changes in the resolution type filter dropdown,
 * updating the suggestion card filter and managing the loading state.
 * @returns {Object} An object containing the handleFilterResolutionTypeChange function.
 * @property {function} handleFilterResolutionTypeChange - Function to handle changes in the resolution type filter.
 */
export const useTrackChangesFilter = () => {
    const {
        setSuggestionCardFilter,
        setIsSuggestionsDataUpdated,
        setIsLoadingCards,
    } = useSuggestionsShallowSelector((s) => ({
        setSuggestionCardFilter: s.setSuggestionCardFilter,
        setIsSuggestionsDataUpdated: s.setIsSuggestionsDataUpdated,
        setIsLoadingCards: s.setIsLoadingCards,
    }))

    /**
     * Handles the change event for the resolution type filter dropdown.
     * @param {React.ChangeEvent<{ value: unknown }>} e - The change event for the dropdown.
     * @returns {void}
     * @callback
     * @description This function updates the suggestion card filter based on the selected resolution type.
     * It fetches track changes data and sorts or renders history suggestions accordingly.
     * @see {@link TrackChangeStatus} for available resolution types.
     * @see {@link TrackChangeStatusLabels} for labels corresponding to resolution types.
     * @see {@link useTreeMutations} for functions to re-render and sort suggestions.
     */
    const handleFilterResolutionTypeChange = React.useCallback(
        (e: React.ChangeEvent<{ value: unknown }>) => {
            const resolutionType = e.target.value as string
            setIsSuggestionsDataUpdated(false)
            setSuggestionCardFilter(resolutionType)
            setIsLoadingCards(true)
        },
        [
            setSuggestionCardFilter,
            setIsSuggestionsDataUpdated,
            setIsLoadingCards,
        ]
    )

    return { handleFilterResolutionTypeChange }
}
